import React, { useEffect, useState } from "react";
import styles from "./GridCard.module.css";
import { Card } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useToggleFavorite } from "../favorites/hooks/useToggleFavorite";
import { useTripPublicationSummaryList } from "../hooks/useTripPublicationSummaryList";
import { useZorni } from "../../../hooks/useZorni";
import { useRequireAuth } from "../../../hooks/useRequireAuth";
import { useVisitedTripStorage } from "../../tripDetails/detailsTrip/hooks/useVisitedTrips";
import { useOperation } from "../../../../dashboard/hooks/useOperation";
import {
  ImpressionTracker,
  ContextPage,
  useFavoriteTracking,
} from "../../../../tracking";

const normalizeLocationKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const resolveLocations = (item) => {
  const rawLocations = Array.isArray(item?.locations)
    ? item.locations
    : Array.isArray(item?.cities)
    ? item.cities
    : Array.isArray(item?.tripProgramDetails)
    ? item.tripProgramDetails.map((step) => step?.city || step?.country)
    : [item?.city];

  const seen = new Set();

  return (rawLocations || []).filter((entry) => {
    const label = String(entry || "").trim();
    if (!label) return false;

    const key = normalizeLocationKey(label);
    if (!key || seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

const GridCard = (props) => {
  const { t } = useTranslation();
  const { userId } = useZorni();
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const { addVisitedTrip } = useVisitedTripStorage(null);
  const { onTotalElementsChange } = props;
  const { trackAddToFavorites } = useFavoriteTracking({ page: ContextPage.HOME });
  const { showToast } = useOperation();

  const CURRENT_PAGE = 1;
  // 15 voyages sur l'accueil : 3 lignes de 5 (maquette « 01 - home v5 »).
  const PAGE_SIZE = 15;

  // Hook favoris
  const { toggleFavorite } = useToggleFavorite();

  // Hook publications
  const { publications, loading, error, totalElements, refetch } =
    useTripPublicationSummaryList({
      currentPage: CURRENT_PAGE,
      elementsPerPage: PAGE_SIZE,
      destination: props.destination,
      startDate: props.startDate,
      endDate: props.endDate,
      adultTravelers: props.adult,
      childTravelers: props.child,
      babiesTravelers: props.baby,
      maxPrice: props.maxPrice,
      minPrice: props.minPrice,
    });

  const [tripPublications, setTripPublications] = useState([]);

  // Sync publications locales
  useEffect(() => {
    if (!loading && !error) {
      setTripPublications(publications);
    }
  }, [publications, loading, error]);

  useEffect(() => {
    if (typeof onTotalElementsChange === "function") {
      onTotalElementsChange(totalElements ?? 0);
    }
  }, [onTotalElementsChange, totalElements]);

  // ⭐ Favoris : toggle
  const handleUpdateFavorite = async (item) => {
    const wasFavorite = Boolean(item?.isFavorite);

    await requireAuth(async () => {
      // Update optimiste
      setTripPublications((prev) =>
        prev.map((p) =>
          p.id === item.id ? { ...p, isFavorite: !p.isFavorite } : p
        )
      );

      // Update backend
      const result = await toggleFavorite(userId, item);

      if (!result?.success) {
        // Annule l'update optimiste puisque le backend a refusé/échoué
        setTripPublications((prev) =>
          prev.map((p) =>
            p.id === item.id ? { ...p, isFavorite: wasFavorite } : p
          )
        );
        showToast(
          t(
            "commons.favoriteError",
            "Impossible de mettre à jour vos favoris pour le moment."
          ),
          "error"
        );
        return;
      }

      await refetch();

      if (!wasFavorite) {
        await trackAddToFavorites({
          establishmentId: item?.establishmentId,
          publicationId: item?.id,
        });
      }
    });
  };

  // Navigation vers le détail
  const handleClick = (item) => {
    addVisitedTrip(item);
    const params = {
      nbAdults: props.adult,
      nbChilds: props.child,
      nbBabies: props.baby,
    };

    const queryString = Object.entries(params)
      .filter(([, value]) => value != null)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    navigate(`trips/${item?.id}/${item?.tripDateId}${queryString ? `/?${queryString}` : ""}`);
  };

  if (loading) {
    return (
      <section className={styles.section_padding_grid_card}>
        <div className={styles.gridContainer}>
          <div className={styles.gridRow}>
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div className={styles.gridCol} key={i}>
                <div className={styles.skeletonPlaceholder} />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section_padding_grid_card}>
      <div className={styles.gridContainer}>
        <div className={styles.gridRow}>
          {tripPublications.map((item) => (
            <div className={styles.gridCol} key={item.id}>
              {(() => {
                const locations = resolveLocations(item);

                return (
              <ImpressionTracker
                publicationId={item.id}
                establishmentId={item.establishmentId}
                page={ContextPage.HOME}
              >
                <Card
                type="offer"
                item={{
                  ...item,
                  city: item?.city || locations.join(" · "),
                  locations,
                }}
                t={t}
                onClick={() => handleClick(item)}
                onUpdateFavorite={() => handleUpdateFavorite(item)}
              />
              </ImpressionTracker>
                );
              })()}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GridCard;
